import { BoxType } from '@lite-workspace/dto-fotoclass-templates';
import type { CustomVectorTemplateBox } from '@lite-workspace/dto-fotoclass-templates';
import type { BoxPassthroughMeta, DrawableVectorData } from '../../interfaces/drawable-data.interface';
import { buildBoxPassthroughMeta, carriedBoxFields, mergeBoxPassthroughMeta } from '../passthrough';
import { buildDrawableEffects, mergeDrawableEffects } from './effects';
import { rectFromBox, rectToBox } from './rect';
import { mergeRotation } from './rotation';
import type { BoxDecoder, BoxEncoder } from './codec-contract';

type VectorFlip = Pick<DrawableVectorData, 'flipHorizontal' | 'flipVertical'>;

/** Renderers read a vector's flip off the drawable, so it leaves the passthrough bag; absent stays absent. */
function vectorFlip(source: VectorFlip): VectorFlip {
  return {
    ...(source.flipHorizontal !== undefined && { flipHorizontal: source.flipHorizontal }),
    ...(source.flipVertical !== undefined && { flipVertical: source.flipVertical }),
  };
}

function vectorPassthroughMeta(box: CustomVectorTemplateBox): BoxPassthroughMeta | undefined {
  const meta = buildBoxPassthroughMeta(box);
  if (!meta) return undefined;
  const { flipHorizontal: _flipHorizontal, flipVertical: _flipVertical, ...rest } = meta;
  return Object.keys(rest).length > 0 ? rest : undefined;
}

export const decode: BoxDecoder<CustomVectorTemplateBox, DrawableVectorData> = (box, ctx) => ({
  id: box.id,
  type: 'vector',
  ...carriedBoxFields(box),
  order: 0, // dispatcher проставляет реальный order по индексу в collage — не забота кодека
  canResize: true,
  opacity: box.opacity,
  blendMode: box.blendMode,
  visible: box.visible,
  locked: box.locked,
  rotation: box.rotation,
  effects: buildDrawableEffects(box.effects),
  vector: box.params,
  ...vectorFlip(box),
  naturalAligns: {
    container: rectFromBox(box.rect, ctx),
    content: box.contentRect ? rectFromBox(box.contentRect, ctx.child('contentRect')) : undefined,
  },
  templateMeta: vectorPassthroughMeta(box),
});

export const encode: BoxEncoder<DrawableVectorData, CustomVectorTemplateBox> = (drawable, ctx) => {
  const box: CustomVectorTemplateBox = {
    type: BoxType.VECTOR,
    id: drawable.id,
    ...carriedBoxFields(drawable),
    rect: rectToBox(drawable.naturalAligns.container, ctx),
    contentRect: drawable.naturalAligns.content ? rectToBox(drawable.naturalAligns.content, ctx) : undefined,
    rotation: mergeRotation(drawable, ctx),
    opacity: drawable.opacity,
    blendMode: drawable.blendMode,
    visible: drawable.visible,
    locked: drawable.locked,
    effects: mergeDrawableEffects(
      drawable.effects,
      drawable.templateMeta?.rawEffects,
      drawable.layerStyleRendering === 'live' ? 'typed' : undefined,
    ),
    params: drawable.vector,
  };

  // The drawable's flip wins over a flip a stale bag (decoded before the flip moved off it) still carries.
  return { ...mergeBoxPassthroughMeta(box, drawable.templateMeta), ...vectorFlip(drawable) };
};
